import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core';
import { withTranslation } from 'react-i18next';

import { SlateDialog } from 'components/Shared/SlateDialog';
import SlateTextInput from 'components/Shared/SlateTextInput';
import p from 'theme/palette';

const useStyles = makeStyles(function() {
  return {
    content: {
      padding: '8px 0 0',
    },
    message: {
      fontFamily: 'Montserrat, sans-serif',
      fontSize: 14,
      lineHeight: 1.6,
      color: p.defaultText,
      marginBottom: 20,
    },
    confirmText: {
      fontFamily: 'Montserrat, sans-serif',
      fontSize: 13,
      lineHeight: 1.6,
      color: p.grey800,
      marginBottom: 12,
    },
    itemName: {
      fontWeight: 600,
      color: p.defaultText,
    },
    inputContainer: {
      marginBottom: 8,
    },
  };
});

/**
 * Reusable verification delete dialog that requires the user to type
 * the item name to confirm deletion.
 */
var VerifyDeleteDialog = function(props) {
  var open = props.open;
  var onClose = props.onClose;
  var onConfirm = props.onConfirm;
  var itemName = props.itemName;
  var itemType = props.itemType;
  var title = props.title;
  var message = props.message;
  var isFetching = props.isFetching;
  var t = props.t;

  var classes = useStyles();
  var inputState = useState('');
  var inputValue = inputState[0];
  var setInputValue = inputState[1];

  var errorState = useState('');
  var error = errorState[0];
  var setError = errorState[1];

  useEffect(function() {
    if (open) {
      setInputValue('');
      setError('');
    }
  }, [open]);

  var handleInputChange = useCallback(function(e) {
    setInputValue(e.target.value);
    if (error) {
      setError('');
    }
  }, [error]);

  var isMatch = inputValue.trim() === itemName.trim();

  var handleConfirm = useCallback(function() {
    if (!isMatch) {
      setError(t('validation:nameMustMatch'));
      return;
    }
    onConfirm({ name: inputValue });
  }, [isMatch, inputValue, onConfirm, t]);

  var handleKeyPress = useCallback(function(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleConfirm();
    }
  }, [handleConfirm]);

  var handleClose = useCallback(function() {
    if (!isFetching) {
      onClose();
    }
  }, [isFetching, onClose]);

  var dialogTitle = title || t('common:confirmDelete');
  var dialogMessage = message || t('common:deleteConfirmMessage', { itemType: itemType || t('common:item') });

  return (
    <SlateDialog
      open={open}
      onClose={handleClose}
      title={dialogTitle}
      closable={!isFetching}
      actions={[
        {
          label: t('btn:cancel'),
          onClick: handleClose,
          variant: 'secondary',
          disabled: isFetching,
          key: 'cancel',
        },
        {
          label: t('btn:delete'),
          onClick: handleConfirm,
          variant: 'destructive',
          disabled: isFetching || !isMatch,
          loading: isFetching,
          key: 'delete',
        },
      ]}
    >
      <div className={classes.content}>
        <p className={classes.message}>
          {dialogMessage}
        </p>
        <p className={classes.confirmText}>
          {t('common:typeNameToConfirm', { name: '' })}
          <span className={classes.itemName}>{itemName}</span>
        </p>
        <div className={classes.inputContainer}>
          <SlateTextInput
            name="confirmName"
            type="text"
            label={t('common:name')}
            placeholder={itemName}
            fullWidth
            value={inputValue}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            error={error}
            disabled={isFetching}
            autoFocus
          />
        </div>
      </div>
    </SlateDialog>
  );
};

VerifyDeleteDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  itemName: PropTypes.string.isRequired,
  itemType: PropTypes.string,
  title: PropTypes.string,
  message: PropTypes.string,
  isFetching: PropTypes.bool,
  t: PropTypes.func.isRequired,
};

VerifyDeleteDialog.defaultProps = {
  itemType: '',
  title: '',
  message: '',
  isFetching: false,
};

export default withTranslation()(VerifyDeleteDialog);
